// Finance build — the internal staff Income Overview at
// /portfolio-admin/income-overview. A whole-book money roll-up over the income
// ledger for a selected window (current month by default): a headline
// Phoenix-retained earnings figure, the four recognized-event buckets, four
// breakdowns (by income type/pool, by channel, by recipient, by period), and a
// by-loan table that drills into each loan's events.
//
// READ-ONLY. The window lives in the URL and is derived at parse time (no
// post-mount default effect). The variance shown everywhere is the
// RECOGNIZED-EVENT variance — outstanding / never-paid amounts live in the
// obligations / cost-recovery ledger (a never-paid period writes no income
// event), hence the cross-link.

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader, Skeleton } from "@/design-system/components/ui";
import {
  INCOME_RANGE_OPTIONS,
  parseRange,
  readIncomeOverviewParams,
} from "@/components/portfolioAdmin/finance/incomeOverview/range";
import { IncomeSummarySplit } from "@/components/portfolioAdmin/finance/incomeOverview/IncomeSummarySplit";
import { IncomeBreakdownSection } from "@/components/portfolioAdmin/finance/incomeOverview/IncomeBreakdownSection";
import { IncomeLoanTable } from "@/components/portfolioAdmin/finance/incomeOverview/IncomeLoanTable";
import { useIncomeOverview } from "@/hooks/portfolioAdmin/useIncomeOverview";

export default function IncomeOverviewPage() {
  const [params, setParams] = useSearchParams();

  // Derive the window at parse time — current month is the default.
  const range = parseRange(params.get("range"));
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";

  const queryParams = useMemo(
    () => readIncomeOverviewParams(params),
    [params],
  );

  const query = useIncomeOverview(queryParams);
  const data = query.data;

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const setRange = (value: string) =>
    writeParams((p) => {
      p.set("range", value);
      // Custom dates only apply to a custom range; drop them otherwise so a
      // stale from/to can't linger on a named window.
      if (value !== "custom") {
        p.delete("from");
        p.delete("to");
      }
    });

  const setDate = (key: "from" | "to", value: string) =>
    writeParams((p) => {
      if (value) p.set(key, value);
      else p.delete(key);
    });

  return (
    <div className="flex flex-col gap-space-5" data-testid="income-overview-page">
      <PageHeader
        eyebrow="Loan financials"
        title="Income Overview"
        description="Whole-book roll-up of recognized income across every loan for the selected window. Read-only — drill into a loan to see its income events and the commissions they generated."
      />

      <div
        className="flex flex-wrap items-end gap-space-3"
        data-testid="income-overview-filters"
      >
        <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
          Range
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
            data-testid="income-overview-range-filter"
          >
            {INCOME_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {range === "custom" && (
          <>
            <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setDate("from", e.target.value)}
                className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
                data-testid="income-overview-from-filter"
              />
            </label>
            <label className="flex flex-col gap-space-1 text-caption text-gray-mid">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => setDate("to", e.target.value)}
                className="rounded-md border border-border-light bg-white-pure px-space-2 py-space-1 text-small text-onyx"
                data-testid="income-overview-to-filter"
              />
            </label>
          </>
        )}
      </div>

      <p className="m-0 text-small text-gray-mid" data-testid="income-overview-variance-note">
        Variance below is the recognized-event variance (expected vs received on
        events that posted). Outstanding or never-paid amounts are not income
        events — they live in the{" "}
        <Link
          to="/portfolio-admin/cost-recovery"
          className="text-gold-shadow hover:underline"
          data-testid="income-overview-cost-recovery-link"
        >
          obligations / cost-recovery ledger
        </Link>
        .
      </p>

      {query.isError ? (
        <div
          className="rounded-lg border border-status-error bg-status-error/12 px-space-4 py-space-3 text-small text-status-error"
          data-testid="income-overview-error"
        >
          Couldn&apos;t load the income overview. Try again.
        </div>
      ) : query.isLoading || !data ? (
        <Skeleton className="h-48 w-full" />
      ) : (
        <>
          <IncomeSummarySplit summary={data.summary} />

          <IncomeBreakdownSection
            title="By income type / pool"
            rows={data.byTypePool}
            dimensionLabel="Income type / pool"
            testId="income-breakdown-type-pool"
          />
          <IncomeBreakdownSection
            title="By channel"
            rows={data.byChannel}
            dimensionLabel="Channel"
            testId="income-breakdown-channel"
          />
          <IncomeBreakdownSection
            title="By recipient"
            rows={data.byRecipient}
            dimensionLabel="Recipient"
            testId="income-breakdown-recipient"
          />
          <IncomeBreakdownSection
            title="By period"
            rows={data.byPeriod}
            dimensionLabel="Period"
            testId="income-breakdown-period"
          />

          <IncomeLoanTable rows={data.byLoan} params={queryParams} />
        </>
      )}
    </div>
  );
}
